import { createClient } from "@/utils/supabase/client";

import { createAttendeeUploadUrl } from "./actions/attendee-actions";
import { ATTENDEE_EXTRACT_API_PATH, ATTENDEE_UPLOAD_BUCKET, MAX_ATTENDEE_DOCUMENT_BYTES } from "./attendee-constants";

/** What the extraction route answers: home cities read by the model, or the document's text. */
interface DocumentResult {
  origins?: Array<{ city: string; count: number }>;
  people?: number;
  text?: string;
  error?: string;
}

/**
 * Sends a PDF or Word attendee list for processing: uploaded straight to storage through a signed
 * URL, then read by the extraction route, which deletes it either way.
 * @param file - The picked document.
 * @param textOnly - Return the document's text instead of the home cities read from it.
 * @returns The route's answer, or `{ error }` with a message for the planner.
 * @throws Never.
 */
export async function readAttendeeDocument(
  file: File,
  textOnly = false
): Promise<{ result: DocumentResult } | { error: string }> {
  if (file.size > MAX_ATTENDEE_DOCUMENT_BYTES) return { error: "Keep PDF and Word files under 10 MB." };
  const target = await createAttendeeUploadUrl({ fileName: file.name, size: file.size, type: file.type });
  if (!target.ok) return { error: target.error };
  const { error } = await createClient()
    .storage.from(ATTENDEE_UPLOAD_BUCKET)
    .uploadToSignedUrl(target.path, target.token, file, { contentType: file.type || "application/octet-stream" });
  if (error) return { error: "The upload didn't go through. Try again." };

  const response = await fetch(ATTENDEE_EXTRACT_API_PATH, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ path: target.path, ...(textOnly ? { textOnly } : {}) }),
  }).catch(() => null);
  const result = (await response?.json().catch(() => null)) as DocumentResult | null;
  if (!response?.ok || !result) {
    return { error: result?.error ?? "Couldn't read that file. Try a CSV or Excel list." };
  }
  return { result };
}
